import type { InspectTimelineEntry } from '../api/types';
import { formatInspectInstant } from './inspect-detail';
import { decodeUrlForDisplay } from './utils';

export interface InspectTimelineChange {
  label: string;
  before: string;
  after: string;
}

function statusLine(entry: InspectTimelineEntry): string {
  const parts = [entry.verdict, entry.coverageState].filter(Boolean);
  return parts.join(' — ') || '—';
}

function formatUrls(urls: string[]): string {
  if (urls.length === 0) return '—';
  return urls.map(decodeUrlForDisplay).join('\n');
}

function formatOptional(
  value: string | null | undefined,
  formatter?: (v: string) => string,
): string {
  if (!value) return '—';
  return formatter ? formatter(value) : decodeUrlForDisplay(value);
}

export function diffTimelineEntries(
  older: InspectTimelineEntry,
  newer: InspectTimelineEntry,
): InspectTimelineChange[] {
  const changes: InspectTimelineChange[] = [];

  const olderStatus = statusLine(older);
  const newerStatus = statusLine(newer);
  if (olderStatus !== newerStatus) {
    changes.push({
      label: 'وضعیت پوشش',
      before: olderStatus,
      after: newerStatus,
    });
  }

  const olderLastmod = formatOptional(older.sitemapLastmod, formatInspectInstant);
  const newerLastmod = formatOptional(newer.sitemapLastmod, formatInspectInstant);
  if (olderLastmod !== newerLastmod) {
    changes.push({
      label: 'lastmod',
      before: olderLastmod,
      after: newerLastmod,
    });
  }

  const olderCrawl = formatOptional(older.lastCrawlTime, formatInspectInstant);
  const newerCrawl = formatOptional(newer.lastCrawlTime, formatInspectInstant);
  if (olderCrawl !== newerCrawl) {
    changes.push({
      label: 'تاریخ خزش گوگل',
      before: olderCrawl,
      after: newerCrawl,
    });
  }

  const olderUserCanon = formatOptional(older.userCanonical);
  const newerUserCanon = formatOptional(newer.userCanonical);
  if (olderUserCanon !== newerUserCanon) {
    changes.push({
      label: 'canonical کاربر',
      before: olderUserCanon,
      after: newerUserCanon,
    });
  }

  const olderGoogleCanon = formatOptional(older.googleCanonical);
  const newerGoogleCanon = formatOptional(newer.googleCanonical);
  if (olderGoogleCanon !== newerGoogleCanon) {
    changes.push({
      label: 'canonical گوگل',
      before: olderGoogleCanon,
      after: newerGoogleCanon,
    });
  }

  const olderRefs = formatUrls(older.referringUrls ?? []);
  const newerRefs = formatUrls(newer.referringUrls ?? []);
  if (olderRefs !== newerRefs) {
    changes.push({
      label: 'صفحات ارجاع‌دهنده',
      before: olderRefs,
      after: newerRefs,
    });
  }

  return changes;
}
